import { findConnectedComponents, type BinaryMask } from './MaskAnalysis';
import { computeBoundingBox } from './RobvisionHelper';

/**
 * Vectorize a binary mask into COCO-style polygons (one flat [x1,y1,x2,y2,…]
 * ring per connected component). Components smaller than `minArea` pixels
 * are dropped as noise.
 *
 * The ring runs along the pixel EDGES (integer lattice coordinates, pixel
 * (x, y) covers [x, x+1)×[y, y+1)), not through pixel centers. Together with
 * a rasterizer that samples pixel centers this makes the save → load
 * round-trip lossless: reopening and re-saving an image must never erode
 * the masks. For that reason simplification is off by default — collinear
 * points are always merged, which keeps straight edges at two vertices;
 * pass `simplifyEpsilon > 0` only where lossy output is acceptable.
 *
 * Known limitation: only outer boundaries are traced — interior holes
 * (e.g. erased inside a mask) are filled in the polygon representation and
 * therefore in the saved/exported dataset. COCO polygon segmentation cannot
 * represent holes; supporting them would require switching the pipeline to
 * RLE masks.
 */
export function maskToCocoPolygons(
  mask: BinaryMask,
  width: number,
  height: number,
  simplifyEpsilon = 0,
  minArea = 16,
): number[][] {
  const cc = findConnectedComponents(mask, width, height, 8);
  const polygons: number[][] = [];
  for (let label = 1; label <= cc.numComponents; label++) {
    if (cc.componentSizes[label - 1] < minArea) {
      continue;
    }
    const ring = traceEdgeBoundary(cc.labelMap, width, height, label);
    if (ring.length < 3) {
      continue;
    }
    const simplified =
      simplifyEpsilon > 0 ? simplifyRing(ring, simplifyEpsilon) : ring;
    if (simplified.length < 3) {
      continue;
    }
    polygons.push(simplified.flat());
  }
  return polygons;
}

/** Axis-aligned COCO bbox [x, y, w, h] of the mask's foreground pixels
 * (RobvisionHelper.computeBoundingBox with the COCO return shape). */
export function maskBBox(
  mask: BinaryMask,
  width: number,
  height: number,
): [number, number, number, number] | null {
  const bbox = computeBoundingBox({
    data:
      mask instanceof Uint8Array
        ? mask
        : new Uint8Array(mask.buffer, mask.byteOffset, mask.length),
    width,
    height,
  });
  return bbox ? [bbox.x, bbox.y, bbox.w, bbox.h] : null;
}

/** Centroid (mean position) of the mask's foreground pixels. */
export function maskCentroid(
  mask: BinaryMask,
  width: number,
  height: number,
): [number, number] | null {
  let sumX = 0;
  let sumY = 0;
  let count = 0;
  for (let y = 0; y < height; y++) {
    const row = y * width;
    for (let x = 0; x < width; x++) {
      if (mask[row + x] > 0) {
        sumX += x;
        sumY += y;
        count++;
      }
    }
  }
  return count === 0 ? null : [sumX / count, sumY / count];
}

/** Whether the mask has any foreground pixel (early-exit scan). */
export function maskHasPixels(mask: BinaryMask): boolean {
  for (let i = 0; i < mask.length; i++) {
    if (mask[i] > 0) {
      return true;
    }
  }
  return false;
}

export function maskArea(mask: BinaryMask): number {
  let area = 0;
  for (let i = 0; i < mask.length; i++) {
    if (mask[i] > 0) {
      area++;
    }
  }
  return area;
}

// Right-hand-rule crack following along the pixel edges of one component:
// the returned ring's vertices are lattice corners (pixel (x, y) covers
// [x, x+1)×[y, y+1)), walked clockwise (screen coordinates, y down) with the
// component always on the right of the walking direction. Consecutive
// collinear corners are merged, so a straight edge costs two vertices.
function traceEdgeBoundary(
  labels: Int32Array,
  width: number,
  height: number,
  label: number,
): Array<[number, number]> {
  const inside = (x: number, y: number) =>
    x >= 0 &&
    y >= 0 &&
    x < width &&
    y < height &&
    labels[y * width + x] === label;

  // Topmost-leftmost pixel of the component; its top-left corner starts the
  // walk. Moving right along its top edge has the component on the right
  // (the pixel itself) and background on the left (row above is empty here).
  let sx = -1;
  let sy = -1;
  outer: for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (labels[y * width + x] === label) {
        sx = x;
        sy = y;
        break outer;
      }
    }
  }
  if (sx === -1) {
    return [];
  }

  // Directions in clockwise order (y down): right, down, left, up.
  const dirs: Array<[number, number]> = [
    [1, 0],
    [0, 1],
    [-1, 0],
    [0, -1],
  ];
  // Pixels flanking the edge a move from corner (x, y) would traverse,
  // relative to the walking direction: [rightPixel, leftPixel].
  const flanks = (x: number, y: number, dir: number): boolean[] => {
    switch (dir) {
      case 0: // right along y: right = below, left = above
        return [inside(x, y), inside(x, y - 1)];
      case 1: // down along x: right = west, left = east
        return [inside(x - 1, y), inside(x, y)];
      case 2: // left along y: right = above, left = below
        return [inside(x - 1, y - 1), inside(x - 1, y)];
      default: // up along x: right = east, left = west
        return [inside(x, y - 1), inside(x - 1, y - 1)];
    }
  };
  // A crack is walkable when the component is on its right and not on its
  // left — the right-hand rule tries the sharpest right turn first.
  const walkable = (x: number, y: number, dir: number): boolean => {
    const [right, left] = flanks(x, y, dir);
    return right && !left;
  };

  const ring: Array<[number, number]> = [[sx, sy]];
  let cx = sx;
  let cy = sy;
  let dir = 0;
  const maxSteps = (width + 1) * (height + 1) * 4;
  for (let step = 0; step < maxSteps; step++) {
    cx += dirs[dir][0];
    cy += dirs[dir][1];
    if (cx === sx && cy === sy) {
      break;
    }
    ring.push([cx, cy]);
    let next = -1;
    for (const turn of [3, 0, 1]) {
      // Counterclockwise turn, straight, clockwise turn — the reverse crack
      // is never walkable (it would need the component on the opposite
      // side). The order only matters at pinch corners (two pixels of the
      // component touching diagonally): both turns are walkable there, and
      // the COUNTERCLOCKWISE one crosses the pinch into the far lobe, so a
      // single self-touching ring covers the whole 8-connected component
      // (proven by the diagonal/pinch round-trip tests). Clockwise-first
      // would close the ring around the near lobe and drop the rest.
      const candidate = (dir + turn) % 4;
      if (walkable(cx, cy, candidate)) {
        next = candidate;
        break;
      }
    }
    if (next === -1) {
      break;
    }
    dir = next;
  }
  // Merge collinear runs: keep a corner only where the direction changes.
  const merged: Array<[number, number]> = [];
  for (let i = 0; i < ring.length; i++) {
    const prev = ring[(i - 1 + ring.length) % ring.length];
    const curr = ring[i];
    const nxt = ring[(i + 1) % ring.length];
    const straight =
      (prev[0] === curr[0] && curr[0] === nxt[0]) ||
      (prev[1] === curr[1] && curr[1] === nxt[1]);
    if (!straight) {
      merged.push(curr);
    }
  }
  return merged;
}

function simplifyRing(
  ring: Array<[number, number]>,
  epsilon: number,
): Array<[number, number]> {
  if (ring.length <= 4) {
    return ring;
  }
  // Split the closed ring into two halves so Douglas-Peucker (an
  // open-polyline algorithm) preserves the overall shape. Splitting at the
  // half-way point is as good as any approximate diameter search.
  const half = Math.floor(ring.length / 2);
  const s1 = douglasPeucker(ring.slice(0, half + 1), epsilon);
  const s2 = douglasPeucker(ring.slice(half).concat([ring[0]]), epsilon);
  // Joint points appear in both halves; drop the duplicates.
  return s1.concat(s2.slice(1, -1));
}

function douglasPeucker(
  points: Array<[number, number]>,
  epsilon: number,
): Array<[number, number]> {
  if (points.length <= 2) {
    return points;
  }
  const [x1, y1] = points[0];
  const [x2, y2] = points[points.length - 1];
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  let maxDist = 0;
  let index = 0;
  for (let i = 1; i < points.length - 1; i++) {
    const [px, py] = points[i];
    const dist =
      len === 0
        ? Math.hypot(px - x1, py - y1)
        : Math.abs(dy * px - dx * py + x2 * y1 - y2 * x1) / len;
    if (dist > maxDist) {
      maxDist = dist;
      index = i;
    }
  }
  if (maxDist <= epsilon) {
    return [points[0], points[points.length - 1]];
  }
  const left = douglasPeucker(points.slice(0, index + 1), epsilon);
  const right = douglasPeucker(points.slice(index), epsilon);
  return left.slice(0, -1).concat(right);
}
